import {
  DragEvent,
  forwardRef,
  KeyboardEvent,
  lazy,
  Suspense,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  completeLine,
  describeGroups,
  helpForGroup,
  parseBucketListing,
  parsePodListing,
} from './completion';
import CliTerminalInput from './CliTerminalInput';
import { AnsiText, CompletionChoices, PromptText } from './CliTerminalText';
import { highlightCommand } from './highlight';
import {
  CliCommandSpec,
  CliEditRequest,
  CliTarget,
  CliTerminalError,
  CliUploadProgress,
  CliUploadRequest,
} from 'types/cli';

const CliFileEditor = lazy(() => import('./CliFileEditor'));

export interface CliTerminalHandle {
  insertLine(template: string): void;
  execute(line: string): void;
  note(text: string): void;
  focus(): void;
}

interface CliTerminalProps {
  clusterName: string;
  target: CliTarget;
  commands: CliCommandSpec[];
  targets?: CliTarget[];
  catalogSource?: 'cli-server' | 'bundled';
  showIntro?: boolean;
  run(
    target: CliTarget,
    line: string,
    onChunk: (text: string) => void,
    options?: { cwd?: string; columns?: number },
  ): Promise<CliUploadRequest | CliEditRequest | void>;
  upload(
    target: CliTarget,
    line: string,
    file: File,
    onProgress: (progress: CliUploadProgress) => void,
    options?: { cwd?: string },
  ): Promise<{ body: string; dest: string; target: string | null }>;
  saveEdit(
    target: CliTarget,
    path: string,
    content: string,
  ): Promise<{ body: string }>;
  cancel(targetId: string): void;
  reconnect(): Promise<unknown>;
  onRunningChange?(running: boolean, line?: string): void;
}

type LineKind = 'prompt' | 'output' | 'error' | 'notice';

interface TerminalLine {
  id: number;
  kind: LineKind;
  text: string;
  command?: string;
  suffix?: string;
  choices?: string[];
}

const BANNER = [
  'Incorta Cloud CLI. No install, no virtualenv, no `ic auth login` -- this session is already',
  'authenticated against this cluster with the access token its owner generated for you. It ends',
  'when the token expires, or when you end the session from the header.',
  '',
  'Each tab above is a pod. Commands typed in a tab run on that pod, and the `exec` prefix is',
  'optional there: `ls /home/incorta` and `tail -f app.log` work as they would in a shell.',
  '`cd` moves the tab into a directory and later commands run from it; `pwd` shows where you are.',
  'Cluster-level groups (`tenant`, `kyuubi`, `data-agent`, ...) work from any tab.',
  'Files: `download PATH...` saves a .tgz; `upload DIR` (or dropping a file here) sends one up;',
  '`cp -i` walks you through an upload or a copy within the node.',
  '`edit FILE` opens a file from the node right here -- change it, Ctrl-S, and it is written back.',
  '',
  'New here? Shortcuts is the runbook: it opens beside this terminal and clicking a step fills in the',
  'prompt for you. Otherwise `help` lists command groups, Tab completes, Up/Down walk history,',
  'and Ctrl-C stops a running command.',
].join('\n');

const podBanner = (target: CliTarget) =>
  target.kind === 'self-managed'
    ? `Self-managed ${target.subClusterType ?? 'sub-cluster'} pod ${target.label}. Bare commands run here.`
    : `${target.label} pod. Bare commands run here.`;

const STREAM_FLUSH_MS = 60;
const MAX_STREAM_CHARS = 200_000;
const MAX_LINES = 500;
const LISTING_TTL_MS = 15_000;
const NO_SUCH_DIRECTORY = '__CLI_NO_DIR__';
const RULER = '0123456789';

const PLACEHOLDER = /\b[A-Z][A-Z_]*\b/;

const usageToLine = (usage: string) =>
  usage
    .replace(/\s*\[[^\]]*\]/g, '')
    .replace(/\.\.\./g, '')
    .replace(/\s+/g, ' ')
    .trim();

let nextLineId = 0;

interface WizardStep {
  ask: string;
  fallback?: string;
  check?: (answer: string) => string | null;
}

interface Wizard {
  steps: WizardStep[];
  answers: string[];
  build: (answers: string[]) => string;
}

const CP_SOURCES = [
  '  1  this computer      (you will pick the file)',
  '  2  a path on the node',
  '',
  'To go the other way -- node to this computer -- use `download PATH...` instead.',
];

const cpWizard = (): Wizard => ({
  answers: [],
  steps: [
    {
      ask: 'source (1/2)',
      check: answer =>
        ['1', '2'].includes(answer)
          ? null
          : 'Answer 1 or 2 (Ctrl-C to cancel).',
    },
  ],
  build: () => '',
});

const cpWizardSteps = (
  source: string,
  cwd: string | null,
): Pick<Wizard, 'steps' | 'build'> => {
  const here = cwd ?? '';
  const required = (what: string) => (answer: string) =>
    answer ? null : `${what} is required (Ctrl-C to cancel).`;
  if (source === '1') {
    return {
      steps: [
        {
          ask: `destination directory on the node${here ? ` [${here}]` : ''}`,
          fallback: here || undefined,
          check: required('A destination'),
        },
      ],
      build: ([dest]) => `upload ${quoteArgument(dest)}`,
    };
  }
  return {
    steps: [
      { ask: 'source path on the node', check: required('A source') },
      { ask: 'destination on the node', check: required('A destination') },
    ],
    build: ([src, dest]) => `cp ${quoteArgument(src)} ${quoteArgument(dest)}`,
  };
};

const quoteArgument = (value: string) =>
  /[\s"']/.test(value) ? `'${value.replace(/'/g, `'"'"'`)}'` : value;

const capStream = (text: string) => {
  if (text.length <= MAX_STREAM_CHARS) return text;
  const trimmed = text.slice(text.length - MAX_STREAM_CHARS);
  const firstBreak = trimmed.indexOf('\n');
  return `... earlier output trimmed ...\n${firstBreak === -1 ? trimmed : trimmed.slice(firstBreak + 1)}`;
};

// Only what came off the pod can carry escape codes; prompts get the shell's colours.
const renderLineText = (line: TerminalLine) => {
  if (line.kind === 'output') return <AnsiText text={line.text} />;
  if (line.kind === 'prompt') return <PromptText text={line.text} />;
  return line.text;
};

const CliTerminal = forwardRef<CliTerminalHandle, CliTerminalProps>(
  function CliTerminal(
    {
      clusterName,
      target,
      commands,
      targets,
      catalogSource,
      showIntro = false,
      run,
      upload,
      saveEdit,
      cancel,
      reconnect,
      onRunningChange,
    },
    ref,
  ) {
    const [lines, setLines] = useState<TerminalLine[]>([
      {
        id: nextLineId++,
        kind: 'notice',
        text: showIntro ? BANNER : podBanner(target),
      },
    ]);
    const [input, setInput] = useState('');
    const [history, setHistory] = useState<string[]>([]);
    const [historyCursor, setHistoryCursor] = useState<number | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const runningLine = useRef<string | undefined>(undefined);
    // Where pod commands run.
    const [cwd, setCwd] = useState<string | null>(null);
    const previousCwd = useRef<string | null>(null);
    const listingCache = useRef(
      new Map<string, { at: number; entries: string[] }>(),
    );

    const inputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const filePick = useRef<((files: File[]) => void) | null>(null);
    const [isDropTarget, setIsDropTarget] = useState(false);
    const [wizard, setWizard] = useState<Wizard | null>(null);
    // The file `edit` opened, while its editor is up over the transcript.
    const [editing, setEditing] = useState<CliEditRequest | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const rulerRef = useRef<HTMLPreElement>(null);
    const stickToBottom = useRef(true);
    const streamingLineId = useRef<number | null>(null);
    const streamBuffer = useRef('');
    const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const shellPrompt = `${target.label}@${clusterName}${cwd ? `:${cwd}` : ''} $`;
    const prompt = wizard
      ? `cp> ${wizard.steps[wizard.answers.length].ask}:`
      : shellPrompt;

    const scrollToBottom = () => {
      const scroller = scrollRef.current;
      if (scroller) scroller.scrollTop = scroller.scrollHeight;
    };

    useEffect(() => {
      if (stickToBottom.current) scrollToBottom();
    }, [lines]);

    // The pod's TTY has no size of its own, so `ls` and `top` lay out for whatever width we report.
    const measureColumns = () => {
      const scroller = scrollRef.current;
      const ruler = rulerRef.current;
      if (!scroller || !ruler) return undefined;
      const charWidth = ruler.getBoundingClientRect().width / RULER.length;
      const { paddingLeft, paddingRight } = getComputedStyle(scroller);
      const usable =
        scroller.clientWidth -
        parseFloat(paddingLeft) -
        parseFloat(paddingRight);
      return charWidth > 0 ? Math.floor(usable / charWidth) : undefined;
    };

    const handleScroll = () => {
      const scroller = scrollRef.current;
      if (!scroller) return;
      stickToBottom.current =
        scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 24;
    };

    useEffect(() => {
      onRunningChange?.(isRunning, runningLine.current);
    }, [isRunning, onRunningChange]);

    useEffect(() => {
      if (catalogSource !== 'bundled') return;
      setLines(prev => [
        ...prev,
        {
          id: nextLineId++,
          kind: 'notice',
          text: 'Note: the command list could not be read from the CLI service, so a cached copy is in use. Recently added commands may be missing.',
        },
      ]);
    }, [catalogSource]);

    // A command still streaming when the dock closes would otherwise keep flushing into unmounted state.
    useEffect(
      () => () => {
        if (flushTimer.current) clearTimeout(flushTimer.current);
      },
      [],
    );

    const write = (kind: LineKind, text: string) =>
      setLines(prev =>
        [...prev, { id: nextLineId++, kind, text }].slice(-MAX_LINES),
      );

    const echoCommand = (
      promptText: string,
      command: string,
      suffix?: string,
    ) =>
      setLines(prev =>
        [
          ...prev,
          {
            id: nextLineId++,
            kind: 'prompt' as LineKind,
            text: `${promptText} `,
            command,
            suffix,
          },
        ].slice(-MAX_LINES),
      );

    const flushStream = () => {
      const pending = streamBuffer.current;
      const id = streamingLineId.current;
      if (!pending || id === null) return;
      streamBuffer.current = '';
      setLines(prev =>
        prev.map(line =>
          line.id === id
            ? { ...line, text: capStream(line.text + pending) }
            : line,
        ),
      );
    };

    const appendToStream = (text: string) => {
      streamBuffer.current += text;
      if (flushTimer.current) return;
      flushTimer.current = setTimeout(() => {
        flushTimer.current = null;
        flushStream();
      }, STREAM_FLUSH_MS);
    };

    const stopStreaming = () => {
      if (flushTimer.current) {
        clearTimeout(flushTimer.current);
        flushTimer.current = null;
      }
      flushStream();
    };

    const groups = useMemo(() => describeGroups(commands), [commands]);

    // The spec the server will resolve a line to, mirroring its group rules.
    const findLockedCommand = (line: string) => {
      const [first, second] = line.split(/\s+/);
      const isGroup = commands.some(command => command.category === first);
      const [category, name] = isGroup
        ? [first, second]
        : [first === 'tail' || first === 'cat' ? 'logs' : 'exec', first];
      const spec = commands.find(
        command => command.category === category && command.command === name,
      );
      return spec && !spec.allowed && !spec.unsupported ? spec : null;
    };

    // preventScroll: the prompt sits at the bottom, and focusing it the default way scrolls it into view.
    const focus = () => inputRef.current?.focus({ preventScroll: true });

    // Clicking anywhere in the transcript puts the cursor back in the prompt.
    const focusUnlessSelecting = () => {
      if (window.getSelection()?.toString()) return;
      focus();
    };

    const insertLine = (template: string) => {
      const line = usageToLine(template);
      const placeholder = PLACEHOLDER.exec(line);
      const text = placeholder ? line : `${line} `;
      setInput(text);
      setHistoryCursor(null);
      setTimeout(() => {
        const field = inputRef.current;
        if (!field) return;
        field.focus();
        if (placeholder)
          field.setSelectionRange(
            placeholder.index,
            placeholder.index + placeholder[0].length,
          );
        else field.setSelectionRange(text.length, text.length);
      }, 0);
    };

    const execute = (line: string) => {
      if (isRunning || wizard || editing) {
        insertLine(line);
        return;
      }
      submitLine(line);
    };

    const note = (text: string) => write('notice', text);

    useImperativeHandle(ref, () => ({ insertLine, execute, note, focus }));

    const runSilently = async (line: string) => {
      let output = '';
      await run(target, line, chunk => (output += chunk), {
        cwd: cwd ?? undefined,
      });
      return output;
    };

    // The pod resolves the directory itself -- `..`, `~`.
    const resolveDirectory = async (path: string) => {
      const output = await runSilently(
        `echo $(cd ${path} 2>/dev/null && pwd || echo ${NO_SUCH_DIRECTORY})`,
      );
      const answer = output
        .split('\n')
        .map(entry => entry.trim())
        .filter(entry => entry && !entry.startsWith('\u2192 '))
        .pop();
      if (!answer || answer === NO_SUCH_DIRECTORY) return null;
      if (!answer.startsWith('/')) throw new Error(answer);
      return answer;
    };

    const changeDirectory = async (argument: string | undefined) => {
      let requested = argument ?? '';
      if (requested === '-') {
        if (!previousCwd.current) {
          write('error', 'cd: no previous directory');
          return;
        }
        requested = previousCwd.current;
      }
      const resolved = await resolveDirectory(requested);
      if (!resolved) {
        write('error', `cd: ${requested || '~'}: No such directory`);
        return;
      }
      previousCwd.current = cwd;
      setCwd(resolved);
    };

    const printWorkingDirectory = async () => {
      const resolved = cwd ?? (await resolveDirectory('.'));
      write('output', resolved ?? '(unknown)');
    };

    const handleBuiltIn = async (line: string) => {
      const [name, argument] = line.split(/\s+/);

      if (name === 'clear') {
        setLines([]);
        return true;
      }

      if (name === 'help' || name === '?') {
        write('notice', argument ? helpForGroup(commands, argument) : groups);
        return true;
      }

      if (name === 'cd' || name === 'pwd') {
        runningLine.current = line;
        setIsRunning(true);
        try {
          if (name === 'cd') await changeDirectory(argument);
          else await printWorkingDirectory();
        } catch (error: any) {
          if (error?.name !== 'AbortError')
            write('error', error?.message ?? `${name} failed.`);
        } finally {
          setIsRunning(false);
          focus();
        }
        return true;
      }

      return false;
    };

    const submit = () => submitLine(input);

    const submitLine = async (raw: string) => {
      const line = raw.trim();
      setInput('');
      setHistoryCursor(null);

      if (wizard) {
        answerWizard(line);
        return;
      }

      if (!line) {
        write('prompt', prompt);
        return;
      }

      setHistory(prev =>
        prev[prev.length - 1] === line ? prev : [...prev, line],
      );
      stickToBottom.current = true;
      echoCommand(prompt, line);
      listingCache.current.clear();

      if (await handleBuiltIn(line)) return;

      const [name, ...rest] = line.split(/\s+/);
      if (
        (name === 'cp' || (name === 'exec' && rest[0] === 'cp')) &&
        (rest.length === (name === 'exec' ? 1 : 0) || rest.includes('-i'))
      ) {
        write(
          'notice',
          ['Where is the file coming from?', ...CP_SOURCES].join('\n'),
        );
        setWizard(cpWizard());
        return;
      }

      await runLine(line);
    };

    const answerWizard = (raw: string) => {
      if (!wizard) return;
      const step = wizard.steps[wizard.answers.length];
      const answer = raw || step.fallback || '';
      write('prompt', `${prompt} ${raw}`);
      const problem = step.check?.(answer);
      if (problem) {
        write('error', problem);
        return;
      }

      const answers = [...wizard.answers, answer];
      if (answers.length === 1 && wizard.steps.length === 1) {
        const next = cpWizardSteps(answers[0], cwd);
        setWizard({
          steps: [...wizard.steps, ...next.steps],
          answers,
          build: all => next.build(all.slice(1)),
        });
        return;
      }
      if (answers.length < wizard.steps.length) {
        setWizard({ ...wizard, answers });
        return;
      }

      const line = wizard.build(answers);
      setWizard(null);
      setHistory(prev =>
        prev[prev.length - 1] === line ? prev : [...prev, line],
      );
      echoCommand(shellPrompt, line);
      listingCache.current.clear();
      runLine(line);
    };

    const runLine = async (line: string, droppedFiles?: File[]) => {
      const locked = findLockedCommand(line);
      if (locked) {
        write(
          'error',
          `${locked.category} ${locked.command} needs ${locked.permission} permission; your role does not include it.`,
        );
        return;
      }

      const streamId = nextLineId++;
      streamingLineId.current = streamId;
      streamBuffer.current = '';
      const streamLine: TerminalLine = {
        id: streamId,
        kind: 'output',
        text: '',
      };
      setLines(prev => [...prev, streamLine].slice(-MAX_LINES));
      runningLine.current = line;
      setIsRunning(true);

      try {
        const request = await run(target, line, appendToStream, {
          cwd: cwd ?? undefined,
          columns: measureColumns(),
        });
        stopStreaming();
        if (request && request.kind === 'upload')
          await uploadFiles(line, request, droppedFiles);
        else if (request && request.kind === 'edit') openEditor(request);
        else if (droppedFiles)
          write(
            'error',
            'The server did not treat this as an upload. Is the CLI service up to date?',
          );
      } catch (error: any) {
        stopStreaming();
        reportFailure(error);
      } finally {
        stopStreaming();
        streamingLineId.current = null;
        setIsRunning(false);
        focus();
      }
    };

    const reportFailure = (error: CliTerminalError & { name?: string }) => {
      if (error?.name === 'AbortError') appendToStream('\n^C');
      else if (error?.code === 'CLI_SESSION_EXPIRED') {
        write('error', 'Your CLI session expired. Reconnecting...');
        reconnect().then(() =>
          write('notice', 'Reconnected. Run the command again.'),
        );
      } else if (
        error?.code === 'CLI_TOKEN_REQUIRED' ||
        error?.code === 'CLI_TOKEN_REJECTED'
      ) {
        // The customer revoked the token, or it expired mid-session. Nothing here can recover it:
        // support has to be handed a new one from the Portal.
        write(
          'error',
          `${error.message} Close this tab and open it again with the new token.`,
        );
      } else write('error', error?.message ?? 'Command failed.');
    };

    // Opens the browser's file picker and resolves with what was chosen -- an empty list when it was dismissed.
    const pickFiles = () =>
      new Promise<File[]>(resolve => {
        const field = fileInputRef.current;
        if (!field) return resolve([]);
        filePick.current = resolve;
        field.value = '';
        const onFocus = () => {
          window.removeEventListener('focus', onFocus);
          setTimeout(
            () => filePick.current === resolve && settleFilePick([]),
            400,
          );
        };
        window.addEventListener('focus', onFocus);
        field.click();
      });

    const settleFilePick = (files: File[]) => {
      const resolve = filePick.current;
      filePick.current = null;
      resolve?.(files);
    };

    const formatBytes = (bytes: number) =>
      bytes < 1024 * 1024
        ? `${Math.max(1, Math.round(bytes / 1024))} KB`
        : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

    const openEditor = (request: CliEditRequest) => {
      write(
        'notice',
        `Editing ${request.path}${request.target ? ` on ${request.target}` : ''} -- Ctrl-S saves it back, Esc closes.`,
      );
      setEditing(request);
    };

    const closeEditor = ({
      saved,
      message,
    }: {
      saved: boolean;
      message: string;
    }) => {
      setEditing(null);
      write(saved ? 'output' : 'notice', message);
      // A saved file changes what `ls` and completion should show for its directory.
      if (saved) listingCache.current.clear();
      focus();
    };

    const uploadFiles = async (
      line: string,
      request: CliUploadRequest,
      dropped?: File[],
    ) => {
      if (request.localPath)
        write(
          'notice',
          `A browser cannot read ${request.localPath} -- choose the file to send instead.`,
        );
      const files = dropped ?? (await pickFiles());
      if (!files.length) {
        write('notice', 'Upload cancelled: no file chosen.');
        return;
      }

      const where = `${request.dest}${request.target ? ` on ${request.target}` : ''}`;
      for (const file of files) {
        const id = nextLineId++;
        const show = (text: string) =>
          setLines(prev =>
            prev.map(entry => (entry.id === id ? { ...entry, text } : entry)),
          );
        setLines(prev =>
          [
            ...prev,
            {
              id,
              kind: 'output' as LineKind,
              text: `Uploading ${file.name} (${formatBytes(file.size)}) to ${where} ...`,
            },
          ].slice(-MAX_LINES),
        );
        const result = await upload(
          target,
          line,
          file,
          ({ part, parts, sent, total }) => {
            if (parts > 1)
              show(
                `Uploading ${file.name}: part ${part} of ${parts} (${formatBytes(sent)} of ${formatBytes(total)}) to ${where} ...`,
              );
          },
          { cwd: cwd ?? undefined },
        );
        show(
          `Uploaded ${file.name} (${formatBytes(file.size)}) to ${where}${result.body && result.body !== 'Success' ? ` -- ${result.body}` : ''}`,
        );
      }
      listingCache.current.clear();
    };

    // Dropping files on the transcript uploads them to the directory the tab is standing in.
    const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDropTarget(false);
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (!files.length || isRunning) return;

      if (!cwd) {
        write(
          'notice',
          'Run `cd DIRECTORY` first so there is somewhere to put the file, or type `upload DIRECTORY` and choose it.',
        );
        return;
      }

      const line = `upload ${cwd}`;
      stickToBottom.current = true;
      echoCommand(
        shellPrompt,
        line,
        `  (${files.map(file => file.name).join(', ')})`,
      );
      setHistory(prev =>
        prev[prev.length - 1] === line ? prev : [...prev, line],
      );
      await runLine(line, files);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
      if (!Array.from(event.dataTransfer?.types ?? []).includes('Files'))
        return;
      event.preventDefault();
      event.dataTransfer.dropEffect = isRunning ? 'none' : 'copy';
      if (!isDropTarget) setIsDropTarget(true);
    };

    const cachedListing = async (
      key: string,
      fetch: () => Promise<string[]>,
    ) => {
      const hit = listingCache.current.get(key);
      if (hit && Date.now() - hit.at < LISTING_TTL_MS) return hit.entries;
      const entries = await fetch();
      listingCache.current.set(key, { at: Date.now(), entries });
      return entries;
    };

    // Directory listings for path completion.
    const listPodDirectory = isRunning
      ? undefined
      : (directory: string) =>
          cachedListing(`pod:${cwd ?? ''}:${directory}`, async () =>
            parsePodListing(await runSilently(`ls -1pA ${directory || '.'}`)),
          );
    const listBucketDirectory = isRunning
      ? undefined
      : (group: string, directory: string) =>
          cachedListing(`${group}:${directory}`, async () =>
            parseBucketListing(
              await runSilently(`${group} ls ${directory || '/'}`),
            ),
          );

    const complete = async (typed: string) => {
      const { line, suggestions } = await completeLine(typed, commands, {
        podTab: true,
        tabGroup: target.node,
        nodeNames: [
          ...new Set(
            (targets ?? [])
              .map(candidate => candidate.node)
              .filter((name): name is string => Boolean(name)),
          ),
        ],
        selfManagedNames: [
          ...new Set(
            (targets ?? [])
              .map(candidate => candidate.self_managed_name)
              .filter((name): name is string => Boolean(name)),
          ),
        ],
        listPodDirectory,
        listBucketDirectory,
      });
      if (inputRef.current?.value !== typed) return;
      setInput(line);
      if (suggestions.length > 1)
        setLines(prev =>
          [
            ...prev,
            {
              id: nextLineId++,
              kind: 'notice' as LineKind,
              text: '',
              choices: suggestions,
            },
          ].slice(-MAX_LINES),
        );
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (
        event.key === 'c' &&
        event.ctrlKey &&
        !window.getSelection()?.toString()
      ) {
        event.preventDefault();
        if (isRunning) cancel(target.id);
        else {
          write('prompt', `${prompt} ${input}^C`);
          setInput('');
          if (wizard) {
            setWizard(null);
            write('notice', 'cp cancelled.');
          }
        }
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        if (!isRunning) submit();
        return;
      }

      if (event.key === 'Tab') {
        event.preventDefault();
        if (!wizard) complete(input);
        return;
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        if (!history.length || wizard) return;
        event.preventDefault();
        const atEnd = historyCursor === null ? history.length : historyCursor;
        const next =
          event.key === 'ArrowUp'
            ? Math.max(0, atEnd - 1)
            : Math.min(history.length, atEnd + 1);
        setHistoryCursor(next);
        setInput(next === history.length ? '' : history[next]);
      }
    };

    const placeholder = isRunning
      ? 'running -- Ctrl-C to stop'
      : wizard
        ? 'type your answer, Enter to continue, Ctrl-C to cancel'
        : '';

    return (
      <>
        <div
          className={`CliTerminal${isDropTarget ? ' CliTerminal--drop-target' : ''}`}
          ref={scrollRef}
          data-testid="cli-terminal-output"
          data-target={target.id}
          onClick={focusUnlessSelecting}
          onScroll={handleScroll}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDropTarget(false)}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            data-testid="cli-terminal-file-input"
            onChange={event =>
              settleFilePick(Array.from(event.target.files ?? []))
            }
          />
          <pre
            ref={rulerRef}
            className="CliTerminal__line CliTerminal__ruler"
            aria-hidden
          >
            {RULER}
          </pre>
          {lines.map(line =>
            line.choices ? (
              <CompletionChoices key={line.id} items={line.choices} />
            ) : (
              <pre
                className={`CliTerminal__line CliTerminal__line--${line.kind}`}
                key={line.id}
              >
                {renderLineText(line)}
                {line.command === undefined
                  ? null
                  : highlightCommand(
                      line.command,
                      commands,
                      true,
                      target.node,
                    ).map((token, index) => (
                      <span
                        className={`CliTerminal__token CliTerminal__token--${token.role}`}
                        key={index}
                      >
                        {token.text}
                      </span>
                    ))}
                {line.suffix}
              </pre>
            ),
          )}

          <CliTerminalInput
            inputRef={inputRef}
            prompt={prompt}
            value={input}
            placeholder={placeholder}
            busy={isRunning}
            onChange={setInput}
            onKeyDown={handleKeyDown}
          />
        </div>
        {editing ? (
          <Suspense fallback={null}>
            <CliFileEditor
              key={editing.path}
              file={editing}
              save={content => saveEdit(target, editing.path, content)}
              close={closeEditor}
            />
          </Suspense>
        ) : null}
      </>
    );
  },
);

export default CliTerminal;
